/**
 * Onboarding create-org route — self-serve admin signup.
 *
 * Guides the new user through creating their first root organization.
 * The form collects:
 *
 * - **Organization name** (required) — human-readable display name.
 * - **Slug** (required, auto-derived from name) — URL-friendly identifier.
 * - **Institution type** (required) — K-12 district, K-12 private, or
 *   higher education.
 *
 * On successful submission the server creates the org and assigns the
 * user as `OWNER`. The user now has `admin` scope, so we redirect to
 * `/admin` via a full-page navigation (cross-zone).
 *
 * @module routes/onboard-create-org
 */

import { tryCatch } from '@learnai/tools/try-catch';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { Input } from '@learnai/ui/components/ui/input';
import { Label } from '@learnai/ui/components/ui/label';
import { NativeSelect } from '@learnai/ui/components/ui/native-select';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { api } from '@web/lib/api';
import { ArrowLeftIcon, BuildingIcon, CheckIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InstitutionOption {
	value: string;
	label: string;
	description: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INSTITUTION_TYPES: InstitutionOption[] = [
	{
		value: 'K12_DISTRICT',
		label: 'K-12 District',
		description: 'A multi-school public school district.',
	},
	{
		value: 'K12_PRIVATE',
		label: 'K-12 Private School',
		description: 'A standalone private or independent school.',
	},
	{
		value: 'HIGHER_EDUCATION',
		label: 'Higher Education',
		description: 'A university, college, or community college.',
	},
];

/**
 * Slug validation regex — must match the server-side `slugSchema` in
 * `packages/server/src/lib/schemas.ts`. Lowercase alphanumeric with
 * hyphens, 2–63 characters.
 */
const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Derives a URL-friendly slug from a human-readable organization name.
 *
 * - Lowercases the input.
 * - Replaces runs of non-alphanumeric characters with a single hyphen.
 * - Trims leading/trailing hyphens.
 * - Truncates to 63 characters (slug max length).
 */
function deriveSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 63);
}

/**
 * Validates a slug and returns an error message, or `null` if valid.
 */
function validateSlug(slug: string): string | null {
	if (slug.length < 2) return 'Slug must be at least 2 characters.';
	if (slug.length > 63) return 'Slug must be at most 63 characters.';
	if (!SLUG_REGEX.test(slug)) return 'Only lowercase letters, numbers, and hyphens (e.g. "acme-district").';
	return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardCreateOrg() {
	const [name, setName] = useState('');
	const [slug, setSlug] = useState('');
	const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
	const [institutionType, setInstitutionType] = useState('');
	const [slugError, setSlugError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);

	// -------------------------------------------------------------------
	// Auto-derive slug from name (unless manually edited)
	// -------------------------------------------------------------------

	useEffect(() => {
		if (!slugManuallyEdited) {
			const derived = deriveSlug(name);
			setSlug(derived);
			setSlugError(derived.length > 0 ? validateSlug(derived) : null);
		}
	}, [name, slugManuallyEdited]);

	const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
		setSlugManuallyEdited(true);
		setSlug(value);
		setSlugError(value.length > 0 ? validateSlug(value) : null);
	}, []);

	const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
	}, []);

	// -------------------------------------------------------------------
	// Form validity
	// -------------------------------------------------------------------

	const trimmedName = name.trim();
	const isValid = trimmedName.length > 0 && slug.length >= 2 && !slugError && institutionType.length > 0;

	// -------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) {
		e.preventDefault();
		e.stopPropagation();

		// Final validation before submission.
		const slugValidation = validateSlug(slug);
		if (slugValidation) {
			setSlugError(slugValidation);
			return;
		}

		if (!isValid) return;

		setLoading(true);

		const { data, error: createErr } = await tryCatch(
			api.organizations.createRoot({
				name: trimmedName,
				slug,
				institutionType: institutionType as 'K12_DISTRICT' | 'K12_PRIVATE' | 'HIGHER_EDUCATION',
			}),
		);

		if (createErr) {
			const message = createErr instanceof Error ? createErr.message : 'Failed to create organization.';

			// Surface slug-conflict errors on the field; others as toasts.
			if (message.toLowerCase().includes('slug')) {
				setSlugError(message);
			} else {
				toast.error(message);
			}

			setLoading(false);
			return;
		}

		if (!data) {
			toast.error('Unexpected error. Please try again.');
			setLoading(false);
			return;
		}

		// Brief success state before redirecting.
		setSuccess(true);

		// The user is now an OWNER of the new org → has `admin` scope.
		// Full-page redirect into the admin zone.
		window.location.href = '/admin';
	}

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------

	return (
		<div className="relative flex min-h-screen w-full items-center justify-center px-4 py-12">
			<nav className="absolute top-6 left-6">
				<a href="/">
					<BrandLogo size="sm" />
				</a>
			</nav>

			<Card className="w-full max-w-md overflow-hidden">
				<CardContent className="p-6 md:p-8">
					<form onSubmit={handleSubmit}>
						<div className="flex flex-col gap-6">
							{/* ── Back link ───────────────────────────── */}
							<div>
								<Link
									to="/onboard"
									className="inline-flex items-center p-2 gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
								>
									<ArrowLeftIcon className="size-3.5" />
									Back
								</Link>
							</div>

							{/* ── Header ──────────────────────────────── */}
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
									<BuildingIcon className="size-6" />
								</div>
								<div>
									<h1 className="text-2xl font-bold">Create your organization</h1>
									<p className="mt-1 text-balance text-muted-foreground">
										Set up your school, district, or institution. You'll be the owner.
									</p>
								</div>
							</div>

							{/* ── Organization name ────────────────────── */}
							<div className="grid gap-2">
								<Label htmlFor="org-name">
									Organization Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="org-name"
									name="org-name"
									type="text"
									placeholder="Springfield School District"
									value={name}
									onChange={handleNameChange}
									required
									autoFocus
									autoComplete="organization"
									maxLength={200}
									disabled={loading || success}
								/>
							</div>

							{/* ── Slug ─────────────────────────────────── */}
							<div className="grid gap-2">
								<Label htmlFor="slug">
									URL Identifier <span className="text-destructive">*</span>
								</Label>
								<Input
									id="slug"
									name="slug"
									type="text"
									placeholder="springfield-school-district"
									value={slug}
									onChange={handleSlugChange}
									required
									maxLength={63}
									aria-invalid={!!slugError}
									disabled={loading || success}
								/>
								{slugError ? (
									<p className="text-xs text-destructive">{slugError}</p>
								) : slug.length >= 2 ? (
									<p className="text-xs text-muted-foreground">
										Your org will be identified as <span className="font-medium text-foreground">{slug}</span>
									</p>
								) : (
									<p className="text-xs text-muted-foreground">Lowercase letters, numbers, and hyphens only.</p>
								)}
							</div>

							{/* ── Institution type ─────────────────────── */}
							<div className="grid gap-2">
								<Label htmlFor="institution-type">
									Institution Type <span className="text-destructive">*</span>
								</Label>
								<NativeSelect
									id="institution-type"
									name="institution-type"
									value={institutionType}
									onChange={(e) => setInstitutionType(e.target.value)}
									required
									disabled={loading || success}
								>
									<option value="" disabled>
										Select type…
									</option>
									{INSTITUTION_TYPES.map((type) => (
										<option key={type.value} value={type.value}>
											{type.label}
										</option>
									))}
								</NativeSelect>
								{institutionType && (
									<p className="text-xs text-muted-foreground">
										{INSTITUTION_TYPES.find((t) => t.value === institutionType)?.description}
									</p>
								)}
							</div>

							{/* ── Submit ──────────────────────────────── */}
							<Button type="submit" className="w-full" disabled={!isValid || loading || success}>
								{success ? (
									<>
										<CheckIcon className="size-4" />
										Created — redirecting…
									</>
								) : loading ? (
									<Spinner className="size-3" />
								) : (
									'Create organization'
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
